/** Reduce Noise — bilateral filter.
 *
 *  Standard bilateral: each output pixel is a weighted average of its
 *  neighbors where the weight is the **product** of (a) a spatial
 *  Gaussian based on pixel-distance and (b) a range Gaussian based on
 *  color-distance. Edge-preserving — pixels on the far side of a
 *  bright edge contribute almost nothing because their range weight
 *  is near zero, so edges stay sharp while smooth regions get the
 *  smoothing they need.
 *
 *  Complexity is `O(W·H·r²)` — not separable in general (range weights
 *  break the separability of the spatial Gaussian). The `radius ≤ 5`
 *  cap keeps the inner loop bounded at 121 samples per pixel; on a
 *  768-px preview that's ~70M ops, well inside the budget for a
 *  per-frame rAF preview. Full-res Apply on 4K is ~2s; acceptable for
 *  a one-shot commit but a future Web Worker offload is the natural
 *  follow-up.
 *
 *  `strength` mixes the bilateral output with the source — gives the
 *  user a "subtle / aggressive" axis without forcing them to fight
 *  with the rangeSigma slider for soft denoising. */

export interface ReduceNoiseValue {
  /** Spatial radius in pixels (1..5). Larger covers more of the
   *  neighborhood but cost grows quadratically. */
  radius: number;
  /** Range sigma in 0..255 color-distance units. Pixels whose RGB
   *  distance from the center exceeds this sigma get exponentially
   *  smaller weight — controls edge preservation. Small values
   *  (10-30) preserve fine edges; large values (80+) effectively
   *  collapse the filter into a plain Gaussian blur. */
  rangeSigma: number;
  /** Mix factor (0..100) between the source and the bilateral
   *  output. 0 = identity, 100 = pure bilateral. */
  strength: number;
}

export const identityReduceNoiseValue = (): ReduceNoiseValue => ({
  radius: 1,
  rangeSigma: 30,
  strength: 0,
});

export function applyReduceNoisePixels(source: ImageData, value: ReduceNoiseValue): ImageData {
  const w = source.width;
  const h = source.height;
  const r = Math.max(0, Math.min(5, Math.floor(value.radius)));
  const strength = Math.max(0, Math.min(100, value.strength));
  if (r === 0 || strength === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const rangeSigma = Math.max(1, value.rangeSigma);
  const spatialSigma = r;
  const twoSpatialSq = 2 * spatialSigma * spatialSigma;
  const twoRangeSq = 2 * rangeSigma * rangeSigma;

  // Pre-compute the spatial kernel — only depends on (dx, dy), so
  // it's the same for every pixel.
  const kSize = 2 * r + 1;
  const spatial = new Float32Array(kSize * kSize);
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      spatial[(dy + r) * kSize + (dx + r)] = Math.exp(-(dx * dx + dy * dy) / twoSpatialSq);
    }
  }

  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const mix = strength / 100;
  const inv = 1 - mix;
  const maxX = w - 1;
  const maxY = h - 1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ci = (y * w + x) * 4;
      const cR = src[ci];
      const cG = src[ci + 1];
      const cB = src[ci + 2];
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let sumW = 0;
      for (let dy = -r; dy <= r; dy++) {
        const sy = Math.min(maxY, Math.max(0, y + dy));
        const rowOff = sy * w;
        const kRow = (dy + r) * kSize;
        for (let dx = -r; dx <= r; dx++) {
          const sx = Math.min(maxX, Math.max(0, x + dx));
          const ni = (rowOff + sx) * 4;
          const nR = src[ni];
          const nG = src[ni + 1];
          const nB = src[ni + 2];
          const dR = cR - nR;
          const dG = cG - nG;
          const dB = cB - nB;
          const rangeW = Math.exp(-(dR * dR + dG * dG + dB * dB) / twoRangeSq);
          const w_total = spatial[kRow + dx + r] * rangeW;
          sumR += nR * w_total;
          sumG += nG * w_total;
          sumB += nB * w_total;
          sumW += w_total;
        }
      }
      // sumW is always > 0 because the center pixel always contributes
      // spatial weight 1 and range weight 1. No divide-by-zero guard
      // needed.
      const bR = sumR / sumW;
      const bG = sumG / sumW;
      const bB = sumB / sumW;
      out[ci] = cR * inv + bR * mix;
      out[ci + 1] = cG * inv + bG * mix;
      out[ci + 2] = cB * inv + bB * mix;
      out[ci + 3] = src[ci + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyReduceNoiseToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ReduceNoiseValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyReduceNoisePixels(source, value), 0, 0);
}
